import React, {useEffect, useMemo, useState} from "react";
import './Timeline.less';
import timelineArrow from '../../../img/timeline-arrow.svg'
import {Button} from "react-bootstrap";
import CreateEvent from "./CreateEvent";
import editIcon from '../../../img/icons/icon-edit.svg'
import deleteIcon from '../../../img/icons/icon-delete.svg'
import ConfirmModal from "../common/ConfirmModal";
import useAPI from "../../hooks/useAPI";
import EditEvent from "./EditEvent";
import {scrollTo} from "react-select/dist/declarations/src/utils";
import useUserContext from "../../hooks/useUserContext";
import {getProgramIndicatorsPerms} from "../../helpers/programIndicators";

export default function Timeline({onScroll} : TimelineOptions) {
    const api = useAPI();
    const userCtx = useUserContext()

    const [events, setEvents] = useState<IEvent[]>([]);
    const [offset, setOffset] = useState<number>(0);
    const [addEventModalActive, setAddEventModalActive] = useState<boolean>(false);
    const [selectedToDeleteEventId, setSelectedToDeleteEventId] = useState<number | null>(null)
    const [selectedToEditEvent, setSelectedToEditEvent] = useState<IEvent | null>(null)

    const todayDate = useMemo(() => new Date(), [])
    const [eventWindowSize, setEventWindowSize] = useState(0)
    const eventsWindow = useMemo<IEvent[]>(
        () => events.slice(events.length - eventWindowSize + offset, events.length + offset),
        [offset, events]
    );
    const perms = useMemo(() => getProgramIndicatorsPerms(userCtx), [userCtx])

    const onAddEvent = async (event: IEvent) => {
        const eventResp = await api.post('/mng/courseEvents/0', event);
        if (eventResp.id) {
            setEvents(prevState => [...prevState, {
                ...event,
                id: eventResp.id,
                date: new Date(eventResp.eventDate)
            }].sort(
                (event1, event2) => event1.date.getTime() - event2.date.getTime()
            ))
        }
    };

    const onRemoveEvent = async () => {
        const eventResp = await api.del(
            `/mng/courseEvents/${selectedToDeleteEventId}`,
        );
        setEvents(prevState => prevState.filter(event => event.id !== selectedToDeleteEventId))
        setSelectedToDeleteEventId(null);
    }

    const onEditEvent = async (event: IEvent) => {
        if (selectedToEditEvent) {
            const eventResp = await api.post(`/mng/courseEvents`, {...event, id: selectedToEditEvent.id});
            setEvents(prevState => [
                ...prevState.filter(event => event.id !== selectedToEditEvent.id),
                {...event, date: new Date(eventResp.eventDate), id: eventResp.id}
            ].sort(
                (event1, event2) => event1.date.getTime() - event2.date.getTime()
            ))
        }
        setSelectedToEditEvent(null);
    };

    const onUploadTimeline = async () => {
        await api.download(
            '/mng/courseEvents/export',
            'Ключевые события в ходе реализации Программы развития.xlsx'
        )
    }

    useEffect((() => {
        const getEvents = async () => {
            const eventsResp = await api.get('/mng/courseEvents');
            const events: IEvent[] = eventsResp.map(event => ({...event, date: new Date(event.eventDate)}))
            const nearestEventIndex = getNearestEvent(events)
            const eventWindowSize = window.screen.width > 1800 ? 5 : 4
            setEventWindowSize(Math.min(eventWindowSize, events.length))
            setOffset(eventWindowSize > events.length ? 0 : nearestEventIndex - (events.length - 1) + Math.floor(eventWindowSize / 2))
            setEvents(events);
        }
        getEvents()
    }), [])

    return (
        <div className='timeline'>
            <div className='timeline-header'>
                <h2 className='timeline-title' style={{fontSize: 20, marginBottom: 0}}>Ключевые события в ходе
                    реализации Программы развития</h2>
                <p className='timeline-anchorLink ms-auto' onClick={() => onScroll('results')}>Перейти к выдающимся результатам СП</p>
                <Button variant='outline-primary ms-3' onClick={onUploadTimeline}>Выгрузить в
                    Excel</Button>
                {perms.canEdit && <Button variant='primary' className='ms-3' onClick={() => setAddEventModalActive(true)}>+ Добавить
                    событие</Button>}
            </div>
            <div className="timeline-events">
                <div className="timeline-row timeline-eventType">
                    <h2>Мероприятия и ключевые события</h2>
                    {eventsWindow.map(event => <div className='event-eventType'>
                        {event.type !== 'report' && <>
                            {perms.canEdit && <div className="event-actions">
                                <button className="event-action">
                                    <img src={editIcon} alt="редактировать"
                                         onClick={() => setSelectedToEditEvent(event)}/>
                                </button>
                                <button className="event-action" onClick={() => setSelectedToDeleteEventId(event.id)}>
                                    <img src={deleteIcon} alt="удалить"/>
                                </button>
                            </div>}
                          <p className='event-name mb-3'>{event.descr}</p>
                          <p
                            className='event-date'>{event.date.getDate()} {months[event.date.getMonth()]} {event.date.getFullYear()}</p>
                        </>}
                    </div>)}
                    <div className="timeline-pseudo"/>
                </div>
                <div className="timeline-row">
                    <button
                        className="timeline-arrow left"
                        onClick={() => setOffset(prevState => --prevState)}
                        disabled={events.length - eventWindowSize + offset <= 0}
                    >
                        <img src={timelineArrow} alt='Влево'/>
                    </button>
                    {eventsWindow.map((event, i) => <div className='event-lineContainer'>
                        <div className="event-passed"
                             style={{backgroundColor: event.date.getTime() > todayDate.getTime() ? 'white' : '#010189'}}/>
                        <div className='event-line'/>
                        {i === 0 && events.length - eventWindowSize + offset > 0 &&
                          <div className='event-sub-line first'/>}
                        {i === eventWindowSize - 1 && <div className='event-sub-line end'/>}
                    </div>)}
                    <button
                        className="timeline-arrow right"
                        onClick={() => setOffset(prevState => ++prevState)}
                        disabled={offset === 0}
                    >
                        <img src={timelineArrow} alt='Вправо'/>
                    </button>
                </div>
                <div className="timeline-row timeline-reportType">
                    <h2>Соглашения и отчетность</h2>
                    {eventsWindow.map(event => <div className='event-reportType'>
                        {event.type === 'report' && <>
                          <p className='event-date'>{event.date.getDate()} {months[event.date.getMonth()]} {event.date.getFullYear()}</p>
                            {perms.canEdit && <div className="event-actions revert">
                                <button className="event-action">
                                    <img src={editIcon} alt="редактировать"
                                         onClick={() => setSelectedToEditEvent(event)}/>
                                </button>
                                <button className="event-action" onClick={() => setSelectedToDeleteEventId(event.id)}>
                                    <img src={deleteIcon} alt="удалить"/>
                                </button>
                            </div>}
                          <p className='event-name mb-3'>{event.descr}</p>
                        </>}
                    </div>)}
                    <div className="timeline-pseudo"/>
                </div>
            </div>
            <CreateEvent
                show={addEventModalActive}
                onHide={() => setAddEventModalActive(false)}
                onSubmit={onAddEvent}
            />
            <EditEvent
                show={selectedToEditEvent !== null}
                event={selectedToEditEvent}
                onSubmit={onEditEvent}
                onHide={() => setSelectedToEditEvent(null)}
            />
            <ConfirmModal
                headerText="Удаление события"
                message="Вы уверены что хотите удалить данное событие?"
                show={selectedToDeleteEventId !== null}
                onConfirm={onRemoveEvent}
                onClose={() => setSelectedToDeleteEventId(null)}
            />
        </div>
    )
}

interface TimelineOptions{
    onScroll: (name: 'results' | 'timeline') => void
}

export interface IEvent {
    descr: string,
    date: Date,
    type: 'event' | 'report',
    id: number
}

const months: string[] = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']

function getNearestEvent(events: IEvent[]): number {
    const today = new Date()
    const eventOffsets = events.map(event => Math.abs(event.date.getTime() - today.getTime()))
    return eventOffsets.findIndex(offset => offset === Math.min(...eventOffsets))
}
